import { DefaultRenderer, MarkerClusterer } from '@googlemaps/markerclusterer';

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import Button from 'components/field/Button';
import Card from 'components/global/Card';
import ContextMenu from 'components/global/ContextMenu';
import FormHeader from 'components/global/FormHeader';
import Typography from 'components/global/Typography';
import { HeaderContainerFormHeader } from 'components/global/styled/FormHeader.styled';
import { FilterType, ListComponents } from 'components/list/DynamicListFilterUtils';
import InfoWindow from 'components/list/DynamicListMapView/InfoWindow';
import Maps from 'components/maps/Maps';
import { getWidgetContainer } from 'components/maps/utils/mapHelpers';

import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';

import { BRANCHES, ORDERS, STORAGE_PUBLIC, WAREHOUSES } from 'services/consts';

import PATHS, { mergePaths } from 'utils/paths';

import {
	faArrowLeft,
	faBoxes,
	faBrightness,
	faDroplet,
	faEye,
	faFlame,
	faPenToSquare,
	faRulerTriangle,
	faStore,
	faWarehouse
} from '@fortawesome/pro-solid-svg-icons';

const OrderHeatmap = () => {
	const axios = useAxiosPrivate();
	const { showModal } = useModalRouter();
	const { t } = useTranslation();

	const maps = window?.google?.maps;
	const [map, setMap] = useState(null);
	const [dataLength, setDataLength] = useState(null);
	const [boundry, setBoundry] = useState({ minLat: 0, maxLat: 0, minLon: 0, maxLon: 0 });
	const [hoverItem, setHoverItem] = useState(null);
	const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
	const [anchorPoint, setAnchorPoint] = useState({ x: 0, y: 0 });

	const heatmap = useRef(null);
	const markerClusterer = useRef(null);

	const [toggleHeatmap, setToggleHeatmap] = useState(true);
	const [toggleOrders, setToggleOrders] = useState(false);
	const [toggleBranches, setToggleBranches] = useState(false);
	const [toggleBranchCoverage, setToggleBranchCoverage] = useState(false);
	const [toggleWarehouses, setToggleWarehouses] = useState(false);
	const [toggleGradient, setToggleGradient] = useState(false);
	const [toggleRadius, setToggleRadius] = useState(false);
	const [toggleOpacity, setToggleOpacity] = useState(false);
	const [infoWindow, setInfoWindow] = useState(null);

	const orderData = useRef([]);
	const orderMarkers = useRef([]);

	const branchData = useRef([]);
	const branchMarkers = useRef([]);
	const branchCoverages = useRef([]);

	const warehouseData = useRef([]);
	const warehouseMarkers = useRef([]);

	const searchAreaBox = useRef(null);

	const columns = useMemo(() => {
		if (!hoverItem) return [];
		if (hoverItem.type === 'order') {
			return [
				{
					Header: 'id',
					accessor: 'orderID',
					filterType: FilterType.string
				},
				{
					Header: 'customerPhone',
					accessor: 'phone',
					filterType: FilterType.string,
					renderCell: ListComponents.PHONE
				},
				{
					Header: 'status',
					accessor: 'status',
					filterType: FilterType.enumMulti,
					renderCell: ListComponents.SHIPMENT_STATUS
				}
			];
		} else if (hoverItem.type === 'branch' || hoverItem.type === 'warehouse') {
			return [
				{
					Header: 'id',
					accessor: 'ID',
					filterType: FilterType.string
				},
				{
					Header: 'name',
					accessor: 'name',
					filterType: FilterType.string
				},
				{
					Header: 'phone',
					accessor: 'phone',
					filterType: FilterType.string,
					renderCell: ListComponents.PHONE
				},
				{
					Header: 'status',
					accessor: 'status',
					filterType: FilterType.enumMulti,
					renderCell: ListComponents.STATUS
				}
			];
		}
	}, [hoverItem]);

	const menuItems = useMemo(() => {
		if (!hoverItem) return [];
		if (hoverItem.type === 'order') {
			return [
				{
					label: 'viewOrder',
					disabled: (row) => Array.isArray(row) && 'errorMsg.selectOnlyOneItem',
					icon: faEye,
					action: (row) => {
						showModal('view-order-form', {
							id: row?.ID
						});
					}
				}
			];
		} else if (hoverItem.type === 'branch') {
			return [
				{
					label: 'viewBranch',
					disabled: (row) => Array.isArray(row) && 'errorMsg.selectOnlyOneItem',
					icon: faEye,
					action: (row) => {
						showModal('branch-form', {
							id: row.ID,
							mode: 'view'
						});
					}
				},
				{
					icon: faPenToSquare,
					label: 'editBranch',
					disabled: (row) => Array.isArray(row) && 'errorMsg.selectOnlyOneItem',
					action: (row) => {
						showModal('branch-form', {
							id: row.ID,
							mode: 'edit'
						});
					}
				}
			];
		} else if (hoverItem.type === 'warehouse') {
			return [
				{
					label: 'viewWarehouse',
					disabled: (row) => Array.isArray(row) && 'errorMsg.selectOnlyOneItem',
					icon: faEye,
					action: (row) => {
						showModal('warehouse-form', {
							id: row.ID,
							mode: 'view'
						});
					}
				},
				{
					icon: faPenToSquare,
					label: 'editWarehouse',
					disabled: (row) => Array.isArray(row) && 'errorMsg.selectOnlyOneItem',
					action: (row) => {
						showModal('warehouse-form', {
							id: row.ID,
							mode: 'edit'
						});
					}
				}
			];
		}
	}, [hoverItem]);

	const drawRectangle = async () => {
		const bounds = map.getBounds();
		const { Rectangle } = await maps.importLibrary('maps');

		const areaBox = new Rectangle({
			strokeColor: '',
			strokeOpacity: 0.8,
			strokeWeight: 2,
			fillColor: 'transparent',
			fillOpacity: 0.35,
			map,
			bounds
		});

		areaBox.setMap(map);

		searchAreaBox.current = areaBox;
	};

	const getOrderDetails = async (id) => {
		const response = await axios.post(ORDERS.GET_ORDER_PIN_DETAILS_BY_ID, { id });
		const { data, status } = response;
		if (status !== 200 || !data || data.length === 0) return null;

		return data;
	};

	const getOrders = async () => {
		const response = await axios.post(ORDERS.GET_HEATMAP_ORDER_LIST, boundry);
		const { data, status } = response;
		if (status !== 200 || !data || data.length === 0) return;

		const markers = data.map((order) => ({
			location: new maps.LatLng(order.latitude, order.longitude),
			weight: 50,
			id: order.id
		}));
		heatmap.current.setData(markers);
		orderData.current = markers;

		setDataLength(data.length);
		drawRectangle();
	};

	const getBranches = async () => {
		const requestJson = {
			paging: { offset: 0, count: 5000 },
			columns: ['ID', 'name', 'phone', 'status', 'servingRadius', 'lat', 'lng'],
			totalRecord: false
		};

		const response = await axios.post(BRANCHES.GET_BRANCH_LIST, requestJson);
		const { data, status } = response;
		if (status !== 200 || !data || !data.data || data.data.length === 0) return;

		const markers = data.data
			.filter((order) => !!order.lat && !!order.lng)
			.map((order) => ({
				location: new maps.LatLng(order.lat, order.lng),
				id: order.ID,
				...order
			}));
		branchData.current = markers;
	};

	const getWarehouses = async () => {
		const requestJson = {
			paging: { offset: 0, count: 5000 },
			columns: ['ID', 'name', 'phone', 'status', 'lat', 'lng'],
			totalRecord: false
		};

		const response = await axios.post(WAREHOUSES.GET_WAREHOUSE_LIST, requestJson);
		const { data, status } = response;
		if (status !== 200 || !data || !data.data || data.data.length === 0) return;

		const markers = data.data
			.filter((order) => !!order.lat && !!order.lng)
			.map((order) => ({
				location: new maps.LatLng(order.lat, order.lng),
				id: order.ID,
				...order
			}));
		warehouseData.current = markers;
	};

	const updateBounds = () => {
		const bounds = map.getBounds();
		setBoundry({
			minLat: bounds.getSouthWest().lat(),
			maxLat: bounds.getNorthEast().lat(),
			minLon: bounds.getSouthWest().lng(),
			maxLon: bounds.getNorthEast().lng()
		});
	};

	const genericToggleFn = async (toggle, setToggle, data, markers, fetchData, type) => {
		if (toggle) {
			if (type === 'order') {
				markerClusterer.current.clearMarkers();
			} else {
				markers.current.forEach((marker) => marker.setMap(null));
				markers.current = [];
			}
		} else {
			const { InfoWindow } = await maps.importLibrary('maps');

			const infoWindow = new InfoWindow();
			setInfoWindow(infoWindow);

			const closeInfoWindow = () => {
				infoWindow.setMap(null);
				setIsContextMenuOpen(false);
			};
			map.addListener('dragstart', () => closeInfoWindow());
			map.addListener('zoom_changed', () => closeInfoWindow());
			map.addListener('click', () => closeInfoWindow());

			infoWindow.addListener('domready', () => {
				const closeIcon = document.getElementsByClassName('gm-ui-hover-effect');
				closeIcon[0].style.display = 'none';
			});
			const { AdvancedMarkerElement } = await maps.importLibrary('marker');

			markers.current = data.map((item) => {
				const markerImage = document.createElement('img');

				markerImage.src = `${STORAGE_PUBLIC}/mapview-icons/${
					type === 'branch' ? 'branchAssigned' : type === 'warehouse' ? 'assignedToWarehouse' : 'order'
				}.svg`;
				const marker = new AdvancedMarkerElement({
					position: new maps.LatLng(item.location.lat(), item.location.lng()),
					content: markerImage,
					map
				});
				if (type !== 'order') marker.setMap(map);

				marker.addListener('click', async () => {
					const loadingContent =
						'<div style="width:250px;height:100px;display:flex;justify-content:center;align-items:center;">TEST TEST TEST <i style="color: #000000;font-size: 18px;padding: 5px 8px;" class="fas fa-spinner fa-spin"></i></div>';
					infoWindow.close();
					infoWindow.setContent(loadingContent);
					const itemData = await fetchData(item.id);
					infoWindow.setContent(markerContainer);
					setHoverItem({ ...itemData, ID: item.id, type });
					infoWindow.open(marker.map, marker);
				});
				return marker;
			});
			if (type === 'order') {
				markerClusterer.current.clearMarkers();
				markerClusterer.current.addMarkers(markers.current);
			}
		}
		setToggle((prev) => !prev);
	};

	const toggleOrdersFn = () =>
		genericToggleFn(toggleOrders, setToggleOrders, orderData.current, orderMarkers, getOrderDetails, 'order');

	const toggleBranchCoverageFn = async () => {
		if (toggleBranchCoverage) {
			branchCoverages.current.forEach((marker) => marker.setMap(null));
			branchCoverages.current = [];
		} else {
			const { Circle } = await maps.importLibrary('maps');

			branchData.current.map((item) => {
				branchCoverages.current.push(
					new Circle({
						strokeColor: item.strokeColor ?? '#00ff00',
						strokeOpacity: item.strokeOpacity ?? 0.3,
						strokeWeight: 1,
						fillColor: item.fillColor ?? '#00ff00',
						fillOpacity: item.fillOpacity ?? 0.3,
						map,
						center: item.location,
						radius: (item.servingRadius ?? 10) * 1000
					})
				);
			});
		}
		setToggleBranchCoverage((prev) => !prev);
	};

	const toggleBranchesFn = () =>
		genericToggleFn(
			toggleBranches,
			setToggleBranches,
			branchData.current,
			branchMarkers,
			(id) => branchData.current.find((a) => a.id === id),
			'branch'
		);

	const toggleWarehousesFn = () =>
		genericToggleFn(
			toggleWarehouses,
			setToggleWarehouses,
			warehouseData.current,
			warehouseMarkers,
			(id) => warehouseData.current.find((a) => a.id === id),
			'warehouse'
		);

	const toggleHeatmapFn = () => {
		if (toggleHeatmap) {
			heatmap.current.setData([]);
			searchAreaBox.current.setMap(null);
			setDataLength(null);
			sideButtonContainer.style.display = 'none';
		} else {
			updateBounds();
			sideButtonContainer.style.display = 'flex';
		}
		setToggleHeatmap((prev) => !prev);
	};

	const changeRadius = () => {
		heatmap.current.set('radius', heatmap.current.get('radius') ? null : 20);
		setToggleRadius((prev) => !prev);
	};

	const changeOpacity = () => {
		heatmap.current.set('opacity', heatmap.current.get('opacity') ? null : 0.2);
		setToggleOpacity((prev) => !prev);
	};

	const changeGradient = () => {
		const gradient = [
			'rgba(0, 255, 255, 0)',
			'rgba(0, 255, 255, 1)',
			'rgba(0, 191, 255, 1)',
			'rgba(0, 127, 255, 1)',
			'rgba(0, 63, 255, 1)',
			'rgba(0, 0, 255, 1)',
			'rgba(0, 0, 223, 1)',
			'rgba(0, 0, 191, 1)',
			'rgba(0, 0, 159, 1)',
			'rgba(0, 0, 127, 1)',
			'rgba(63, 0, 91, 1)',
			'rgba(127, 0, 63, 1)',
			'rgba(191, 0, 31, 1)',
			'rgba(255, 0, 0, 1)'
		];

		heatmap.current.set('gradient', heatmap.current.get('gradient') ? null : gradient);
		setToggleGradient((prev) => !prev);
	};

	const infoContainer = useMemo(() => getWidgetContainer('auto'), []);
	const buttonContainer = useMemo(() => getWidgetContainer(), []);
	const sideButtonContainer = useMemo(() => getWidgetContainer('auto', true), []);
	const markerContainer = useMemo(() => getWidgetContainer('100%', false, true), []);

	const initializeMap = async () => {
		map.controls[maps.ControlPosition.BOTTOM_CENTER].push(infoContainer);
		map.controls[maps.ControlPosition.TOP_CENTER].push(buttonContainer);
		map.controls[maps.ControlPosition.RIGHT_CENTER].push(sideButtonContainer);
		const { HeatmapLayer } = await maps.importLibrary('visualization');

		heatmap.current = new HeatmapLayer({
			radius: 15,
			opacity: 0.5,
			map,
			maxIntensity: 100
		});

		map.addListener('dragstart', () => {
			if (heatmap.current.getMap()) heatmap.current.setMap(null);
		});

		map.addListener('idle', () => {
			if (!heatmap.current.getMap()) heatmap.current.setMap(map);
		});

		map.addListener('zoom_changed', () => {
			if (heatmap.current.getMap()) heatmap.current.setMap(null);
		});

		const markerRenderer = new DefaultRenderer();
		const { AdvancedMarkerElement } = await maps.importLibrary('marker');
		markerRenderer.render = function ({ count, position }) {
			const url =
				count < 30
					? `${STORAGE_PUBLIC}/mapview-icons/low.svg`
					: count > 60
						? `${STORAGE_PUBLIC}/mapview-icons/high.svg`
						: `${STORAGE_PUBLIC}/mapview-icons/mid.svg`;
			const size = count < 30 ? 30 : count > 60 ? 36 : 32;

			const markerImage = document.createElement('div');

			markerImage.innerHTML = `
					<div style="position: relative;">
						<img src=${url} style="width: 100%; height: 100%;"></img>
						<span style="position: absolute; top: 45%; left: 50%; transform: translate(-50%, -50%); color: white; font-weight: bold;">${count}</span>
					</div>
					`;

			markerImage.style.width = `${size}px`;
			markerImage.style.height = `${size}px`;

			const clusterMarker = new AdvancedMarkerElement({
				position,
				content: markerImage,
				// adjust zIndex to be above other markers
				zIndex: Number(maps.Marker.MAX_ZINDEX) + count,
				title: `${t('mapview.order.clusterCount')} ${count}`
			});
			return clusterMarker;
		};

		// create cluster instance
		markerClusterer.current = new MarkerClusterer({
			map,
			renderer: markerRenderer
		});

		updateBounds();

		getBranches();
		getWarehouses();
	};

	useEffect(() => {
		getOrders();
	}, [boundry]);

	useEffect(() => {
		if (map && maps) {
			maps.event.addListenerOnce(map, 'idle', () => {
				initializeMap();
			});
		} else {
			// reset all states
			markerClusterer.current = null;
			setToggleHeatmap(true);
			setToggleOrders(false);
			setToggleBranches(false);
			setToggleBranchCoverage(false);
			setToggleWarehouses(false);
		}
	}, [map]);

	return (
		<div>
			<HeaderContainerFormHeader>
				<FormHeader
					lightTitle="reports"
					boldTitle="heatmap"
					icon={faArrowLeft}
					backUrl={mergePaths(PATHS.REPORTS, PATHS.ORDER_REPORTS)}
				/>
			</HeaderContainerFormHeader>
			<Maps setMap={setMap} />
			{isContextMenuOpen && (
				<ContextMenu
					menuItems={menuItems}
					row={hoverItem}
					anchorPoint={anchorPoint}
					onClose={() => setIsContextMenuOpen(false)}
					isContextMenuOpen={isContextMenuOpen}
					setIsContextMenuOpen={setIsContextMenuOpen}
				/>
			)}
			{dataLength !== null &&
				createPortal(
					<Card>
						<Typography>
							{t('orderFoundInThisArea', {
								orderCount: dataLength
							})}
						</Typography>
					</Card>,
					infoContainer
				)}
			{createPortal(
				<Button
					variant={!toggleHeatmap ? 'black' : 'blue'}
					onClick={toggleHeatmapFn}
					label="heatmap"
					icon={faFlame}
					style={{ marginRight: '1rem' }}
				/>,
				buttonContainer
			)}
			{createPortal(
				<Button
					variant={!toggleOrders ? 'black' : 'blue'}
					onClick={toggleOrdersFn}
					label="orders"
					icon={faBoxes}
					style={{ margin: '0 1rem' }}
				/>,
				buttonContainer
			)}
			{createPortal(
				<Button
					variant={!toggleBranches ? 'black' : 'blue'}
					onClick={toggleBranchesFn}
					label="branches"
					icon={faStore}
					style={{ margin: '0 1rem' }}
				/>,
				buttonContainer
			)}
			{createPortal(
				<Button
					variant={!toggleWarehouses ? 'black' : 'blue'}
					onClick={toggleWarehousesFn}
					label="warehouses"
					icon={faWarehouse}
					style={{ marginLeft: '1rem' }}
				/>,
				buttonContainer
			)}
			{createPortal(
				<Button
					variant={!toggleBranchCoverage ? 'black' : 'blue'}
					onClick={toggleBranchCoverageFn}
					label="branchCoverage"
					icon={faStore}
					style={{ margin: '0 1rem' }}
				/>,
				buttonContainer
			)}
			{createPortal(
				<Button variant={!toggleGradient ? 'black' : 'blue'} onClick={changeGradient} icon={faDroplet} />,
				sideButtonContainer
			)}
			{createPortal(
				<Button variant={!toggleRadius ? 'black' : 'blue'} onClick={changeRadius} icon={faRulerTriangle} />,
				sideButtonContainer
			)}
			{createPortal(
				<Button variant={!toggleOpacity ? 'black' : 'blue'} onClick={changeOpacity} icon={faBrightness} />,
				sideButtonContainer
			)}
			{createPortal(
				<InfoWindow
					onClose={() => {
						setIsContextMenuOpen(false);
						infoWindow.close();
					}}
					columns={columns}
					hoveredMarker={hoverItem}
					onContextMenu={(e) => {
						setAnchorPoint({ x: e.pageX, y: e.pageY });
						setIsContextMenuOpen(true);
					}}
				/>,
				markerContainer
			)}
		</div>
	);
};

export default OrderHeatmap;
